import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, Search, Filter as FilterIcon, Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Componente Optimizado de Lista de Pólizas
 * 
 * Mejoras UI:
 * - Selección múltiple con checkboxes
 * - Formato de moneda y alineación derecha
 * - Manejo de campos vacíos con guiones
 */
export default function PoliciesListOptimized() {
  const navigate = useNavigate();
  
  // Estados
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selección Múltiple
  const [selectedPolicies, setSelectedPolicies] = useState(new Set());
  
  // Paginación (controlada por servidor)
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;
  
  // Filtros
  const [filters, setFilters] = useState({
    insurer: '',
    status: '',
    policyType: '',
    search: ''
  });
  
  const [filterOptions, setFilterOptions] = useState({
    insurers: [],
    statuses: [],
    policyTypes: []
  });

  // Cargar opciones al montar
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Cargar pólizas al cambiar filtros o página
  useEffect(() => {
    fetchPolicies();
  }, [page, filters]);

  // Limpiar selección al cambiar de página o filtros
  useEffect(() => {
    setSelectedPolicies(new Set());
  }, [page, filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/policies-optimized/filters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (filters.insurer) params.append('insurer', filters.insurer);
      if (filters.status) params.append('status', filters.status);
      if (filters.policyType) params.append('policy_type', filters.policyType);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${API_URL}/api/policies-optimized/list?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPolicies(response.data.policies);
      setTotalCount(response.data.totalCount);
      setTotalPages(response.data.totalPages);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching policies:', error);
      setError(error.response?.data?.detail || 'Error al cargar pólizas');
      setLoading(false);
    }
  };

  // --- Lógica de Selección ---

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Seleccionar todo lo visible en la página actual
      const allIds = policies.map(p => p.id);
      setSelectedPolicies(new Set(allIds));
    } else {
      setSelectedPolicies(new Set());
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedPolicies);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPolicies(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedPolicies.size === 0) return;
    
    // NOTA: El backend requiere contraseña para borrar. 
    // Aquí usamos un prompt simple, idealmente sería un modal.
    const password = window.prompt(`¿Estás seguro de eliminar ${selectedPolicies.size} pólizas? Ingresa tu contraseña para confirmar:`);
    
    if (!password) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/policies/bulk-delete`,
        { 
          policy_ids: Array.from(selectedPolicies),
          password: password 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${selectedPolicies.size} pólizas eliminadas correctamente`);
      setSelectedPolicies(new Set());
      fetchPolicies(); // Recargar tabla
    } catch (error) {
      console.error('Error deleting policies:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar pólizas');
      setLoading(false); // Solo quitar loading si falló, si tuvo éxito fetchPolicies lo manejará
    }
  };

  // --- Helpers de UI ---

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ insurer: '', status: '', policyType: '', search: '' });
    setPage(1);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // ============= RENDERIZADO =============

  if (error && !loading) {
    return (
      <div className="bg-white shadow-sm rounded-lg border border-red-200 p-12 text-center">
        <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error al cargar pólizas</h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button onClick={() => fetchPolicies()} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barra de Filtros */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <FilterIcon className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filtros:</span>
          </div>
          
          {/* Botón de Acción Masiva (Visible solo si hay selección) */}
          {selectedPolicies.size > 0 && (
            <Button 
              onClick={handleBulkDelete} 
              variant="destructive" 
              size="sm" 
              className="gap-2 animate-in fade-in zoom-in duration-200"
            >
              <Trash2 className="h-4 w-4" />
              Eliminar ({selectedPolicies.size}) Seleccionados
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <select
            value={filters.insurer}
            onChange={(e) => handleFilterChange('insurer', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todas las aseguradoras</option>
            {filterOptions.insurers.map(i => <option key={i} value={i}>{i}</option>)}
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los estados</option>
            {filterOptions.statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <select
            value={filters.policyType}
            onChange={(e) => handleFilterChange('policyType', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los tipos</option>
            {filterOptions.policyTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {(filters.search || filters.insurer || filters.status || filters.policyType) && (
          <div className="mt-3">
            <Button onClick={clearFilters} variant="outline" size="sm">Limpiar Filtros</Button>
          </div>
        )}
      </div>

      <div className="text-sm text-slate-600 flex justify-between items-center">
        <span>Mostrando {policies.length} de {totalCount} pólizas</span>
        {selectedPolicies.size > 0 && (
          <span className="text-indigo-600 font-medium">{selectedPolicies.size} seleccionadas</span>
        )}
      </div>

      {/* Tabla de Pólizas */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        {policies.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {loading ? 'Cargando...' : 'No se encontraron pólizas con los filtros aplicados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-center w-12">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      checked={policies.length > 0 && selectedPolicies.size === policies.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Número</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Aseguradora</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Vencimiento</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Prima Anual</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {policies.map((policy) => (
                  <tr 
                    key={policy.id} 
                    onClick={() => navigate(`/policies/${policy.id}`)}
                    className={`cursor-pointer hover:bg-slate-100 transition-colors ${selectedPolicies.has(policy.id) ? 'bg-indigo-50/50' : ''}`}
                  >
                    <td className="px-6 py-4 text-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        checked={selectedPolicies.has(policy.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelectOne(policy.id)}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{policy.policy_number || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{policy.client_name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{policy.insurer || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{policy.policy_type || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center ${
                        ['En Vigor', 'Vigente', 'Activa'].includes(policy.status) 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {policy.status || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{policy.end_date || '-'}</td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900 text-right">
                      {formatCurrency(policy.annual_premium)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/policies/${policy.id}`);
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-500 hover:text-indigo-600"
                          title="Ver detalles"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/policies/${policy.id}/edit`);
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-500 hover:text-indigo-600"
                          title="Editar póliza"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <div className="text-sm text-slate-600">
            Página {page} de {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              variant="outline"
              size="sm"
            >
              Anterior
            </Button>
            <Button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              variant="outline"
              size="sm"
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Indicador de carga */}
      {loading && policies.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in slide-in-from-bottom-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">Actualizando...</span>
        </div>
      )}
    </div>
  );
}